import React, { useState } from 'react';
import { UnifiedTransaction } from '../types/payment';
import { TransactionAPI } from '../services/api';
import { AlertService } from '../services/AlertService';

interface VoidRefundPanelProps {
  transaction: UnifiedTransaction;
  isOpen: boolean;
  onClose: () => void;
  actionType: 'void' | 'refund';
  onSuccess: () => void;
}

export const VoidRefundPanel: React.FC<VoidRefundPanelProps> = ({
  transaction,
  isOpen,
  onClose,
  actionType,
  onSuccess
}) => {
  const [reason, setReason] = useState('');
  const [confirmation, setConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!reason.trim() || reason.length < 5) {
      AlertService.error('Please provide a detailed reason (at least 5 characters)');
      return;
    }

    if (!confirmation) {
      AlertService.error('Please confirm you want to proceed');
      return;
    }

    try {
      setLoading(true);
      
      if (actionType === 'void') {
        await TransactionAPI.voidTransaction(transaction.id, reason, confirmation);
        AlertService.success('Transaction voided successfully. Funds released immediately.');
      } else {
        await TransactionAPI.refundTransaction(transaction.id, reason, confirmation);
        AlertService.success('Transaction refunded successfully. Refund will appear in customer\'s account in 3-7 business days.');
      }
      
      onSuccess();
      onClose();
      setReason('');
      setConfirmation(false);
      
    } catch (error: any) {
      console.error(`Failed to ${actionType} transaction:`, error);
      AlertService.error(error.message || `Failed to ${actionType} transaction`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setReason('');
      setConfirmation(false);
    }
  };

  const isVoid = actionType === 'void';
  const title = isVoid ? 'Void Transaction' : 'Refund Transaction';
  const actionVerb = isVoid ? 'void' : 'refund';
  const timeframe = isVoid ? 'immediately' : 'in 3-7 business days';

  return (
    <div className="void-refund-overlay" onClick={handleClose}>
      <div className="void-refund-panel" onClick={e => e.stopPropagation()}>
        <div className="panel-header">
          <h2>{title}</h2>
          <button className="btn btn-ghost btn-sm close-btn" onClick={handleClose} disabled={loading}>
            ×
          </button>
        </div>

        <div className="panel-content">
          {/* Transaction Summary */}
          <div className="transaction-summary">
            <h3>Transaction Details</h3>
            <div className="summary-grid">
              <div>
                <label>Transaction ID:</label>
                <span className="monospace">{transaction.id}</span>
              </div>
              <div>
                <label>Amount:</label>
                <span className="amount">${Number(transaction.amount).toFixed(2)}</span>
              </div>
              <div>
                <label>Customer:</label>
                <span>{transaction.first_name} {transaction.last_name}</span>
              </div>
              <div>
                <label>Date:</label>
                <span>{new Date(transaction.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>

          {/* Warning/Disclaimer */}
          <div className={`disclaimer ${isVoid ? 'void-warning' : 'refund-warning'}`}>
            <h4>⚠️ Important Notice</h4>
            {isVoid ? (
              <div>
                <p><strong>Voiding this transaction will:</strong></p>
                <ul>
                  <li>Cancel the transaction immediately</li>
                  <li>Release funds to customer's account instantly</li>
                  <li>Cannot be undone once processed</li>
                  <li>Is only available for same-day transactions</li>
                </ul>
              </div>
            ) : (
              <div>
                <p><strong>Refunding this transaction will:</strong></p>
                <ul>
                  <li>Return ${Number(transaction.amount).toFixed(2)} to the customer</li>
                  <li>Take 3-7 business days to appear in customer's account</li>
                  <li>Cannot be undone once processed</li>
                  <li>Processing fees may apply</li>
                </ul>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="reason">Reason for {actionType} *</label>
              <textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={`Please explain why you are ${actionType}ing this transaction...`}
                rows={4}
                maxLength={500}
                required
                disabled={loading}
                className={reason.length < 5 && reason.length > 0 ? 'error' : ''}
              />
              <small className="char-count">
                {reason.length}/500 characters (minimum 5 required)
              </small>
            </div>

            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={confirmation}
                  onChange={(e) => setConfirmation(e.target.checked)}
                  disabled={loading}
                  required
                />
                <span className="checkmark"></span>
                I understand this action cannot be undone and confirm I want to {actionVerb} this transaction.
              </label>
            </div>

            <div className="button-group">
              <button
                type="button"
                onClick={handleClose}
                className="btn btn-outline-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn ${isVoid ? 'btn-warning' : 'btn-danger'}`}
                disabled={loading || !reason.trim() || reason.length < 5 || !confirmation}
              >
                {loading ? 'Processing...' : `${isVoid ? 'Void' : 'Refund'} Transaction`}
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .void-refund-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 2rem;
        }

        .void-refund-panel {
          background: var(--bg-card);
          border-radius: 12px;
          width: 100%;
          max-width: 600px;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 2rem;
          border-bottom: 1px solid var(--border-primary);
          background: var(--bg-secondary);
        }

        .panel-header h2 {
          margin: 0;
          color: var(--text-primary);
          font-size: 1.5rem;
          font-weight: 600;
        }

        .close-btn {
          font-size: 1.5rem;
          width: 2.5rem;
          height: 2.5rem;
        }

        .panel-content {
          padding: 2rem;
          max-height: calc(90vh - 100px);
          overflow-y: auto;
        }

        .transaction-summary {
          margin-bottom: 2rem;
          padding: 1.5rem;
          background: var(--bg-secondary);
          border-radius: 8px;
          border: 1px solid var(--border-primary);
        }

        .transaction-summary h3 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-size: 1.125rem;
          font-weight: 600;
        }

        .summary-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 1rem;
        }

        .summary-grid > div {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }

        .summary-grid label {
          font-weight: 500;
          color: var(--text-secondary);
          font-size: 0.875rem;
        }

        .summary-grid span {
          color: var(--text-primary);
          font-weight: 500;
        }

        .monospace {
          font-family: 'Courier New', monospace;
        }

        .amount {
          color: var(--success);
          font-weight: 600;
          font-size: 1rem;
        }

        .disclaimer {
          margin-bottom: 2rem;
          padding: 1.5rem;
          border-radius: 8px;
          border-left: 4px solid var(--warning);
        }

        .void-warning {
          background: var(--warning-light);
          border-left-color: var(--warning);
        }

        .refund-warning {
          background: var(--danger-light);
          border-left-color: var(--danger);
        }

        .disclaimer h4 {
          margin: 0 0 1rem 0;
          color: var(--text-primary);
          font-size: 1rem;
          font-weight: 600;
        }

        .disclaimer p {
          margin: 0 0 0.5rem 0;
          color: var(--text-primary);
          font-weight: 500;
        }

        .disclaimer ul {
          margin: 0.5rem 0 0 1rem;
          color: var(--text-secondary);
        }

        .disclaimer li {
          margin-bottom: 0.25rem;
        }

        .form-group {
          margin-bottom: 1.5rem;
        }

        .form-group label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 500;
          color: var(--text-primary);
        }

        .form-group textarea {
          width: 100%;
          padding: 0.75rem;
          border: 1px solid var(--border-primary);
          border-radius: 4px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.875rem;
          font-family: inherit;
          resize: vertical;
          min-height: 100px;
        }

        .form-group textarea:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 2px var(--primary-light);
        }

        .form-group textarea.error {
          border-color: var(--danger);
          box-shadow: 0 0 0 2px var(--danger-light);
        }

        .char-count {
          display: block;
          margin-top: 0.5rem;
          color: var(--text-secondary);
          font-size: 0.75rem;
          text-align: right;
        }

        .checkbox-label {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          cursor: pointer;
          line-height: 1.4;
          font-size: 0.875rem;
        }

        .checkbox-label input[type="checkbox"] {
          margin: 0;
          width: 1rem;
          height: 1rem;
          flex-shrink: 0;
        }

        .button-group {
          display: flex;
          gap: 1rem;
          justify-content: flex-end;
          padding-top: 1rem;
          border-top: 1px solid var(--border-primary);
        }

        .button-group button {
          padding: 0.75rem 1.5rem;
          border-radius: 4px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .button-group button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        @media (max-width: 768px) {
          .void-refund-overlay {
            padding: 1rem;
          }

          .panel-content {
            padding: 1.5rem;
          }

          .summary-grid {
            grid-template-columns: 1fr;
          }

          .button-group {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};